import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowLeft, ArrowRight, KeyRound } from 'lucide-react'
import { ApiError, api } from '../lib/api'
import { ROLE_PARTICIPANT } from '../lib/types'
import { takeSessionLostNotice, toSession, useSession } from '../lib/session'
import { Button } from '../ui/Button'
import { Field, OtpInput } from '../ui/Field'
import { ApiModeBanner } from '../ui/Chrome'
import { APP_LABEL } from '../lib/app-version'
import { EVENT } from '../config/event'
import { useToast } from '../ui/Feedback'

/**
 * One adaptive sign-in flow, not a row of method tabs.
 *
 * The old screen asked people to pick a mechanism before it knew anything about them,
 * and then had to recover with "This account signs in with a password. Switch to the
 * Password tab." — because `/otp` answers role 4 (venue staff) without ever sending a
 * code. Asking for the address first removes the choice: the API's answer decides
 * whether the next step is a six-digit code or a password.
 *
 * That also means the password step cannot simply be deleted. It is no longer a tab,
 * but it is still the only way `admin@cpd.com` and friends can reach /venue/*.
 */
type Stage = 'email' | 'code' | 'password'

/*
 * The code step survives a reload.
 *
 * Reading the code means switching to Gmail, and on a phone that is enough for
 * Android to discard this tab and reload it on the way back - which reset the flow
 * to the email field and made the delegate request a second code. The step, the
 * address and when the code was sent go to sessionStorage (this tab only, gone when
 * it closes) and are restored while the two-minute code is still live. The code
 * itself is never stored.
 */
const RESUME_KEY = 'cpd.login.resume.v1'
const OTP_TTL_MS = 120_000

function readResume(): { stage: Stage; email: string; sentAt: number } | null {
  try {
    const raw = window.sessionStorage.getItem(RESUME_KEY)
    if (!raw) return null
    const v = JSON.parse(raw) as { stage?: Stage; email?: string; sentAt?: number }
    if (v.stage !== 'code' || typeof v.email !== 'string' || typeof v.sentAt !== 'number') return null
    if (Date.now() - v.sentAt > OTP_TTL_MS) { window.sessionStorage.removeItem(RESUME_KEY); return null }
    return { stage: 'code', email: v.email, sentAt: v.sentAt }
  } catch {
    return null
  }
}

function writeResume(value: { email: string; sentAt: number } | null) {
  try {
    if (value) window.sessionStorage.setItem(RESUME_KEY, JSON.stringify({ stage: 'code', ...value }))
    else window.sessionStorage.removeItem(RESUME_KEY)
  } catch {
    // Storage blocked: the flow still works, it just does not survive a reload.
  }
}

export function LoginRoute() {
  const [resume] = useState(readResume)
  const [stage, setStage] = useState<Stage>(resume?.stage ?? 'email')
  const [sessionLost] = useState(() => takeSessionLostNotice())
  const [email, setEmail] = useState(resume?.email ?? '')

  return (
    <>
      <ApiModeBanner />
      <div className="login">
        {/*
          Cover and auth panel share one clipped, rounded container so they read as a
          single printed sheet. Previously the panel was inset and overlapped the
          cover, which left cobalt shoulders poking out either side of the white card.
        */}
        <div className="login__sheet">
        <section className="login__cover" aria-labelledby="conference-title">
          <div className="login__grain" aria-hidden="true" />

          <div className="login__identity">
            <img
              className="login__institution-mark"
              src="/white-font.svg"
              alt="UiTM Pusat Kesihatan emblem"
              width="204"
              height="88"
              decoding="async"
            />
          </div>

          {/*
            The mark carries the name delegates actually use, on one line. The previous
            version set the full Malay title at poster scale and ran to four lines,
            which pushed the sign-in field off the fold on a phone. The full name still
            lives inside the h1, so the accessible name is complete.
          */}
          <div className="login__cover-body">
            {/*
              * From the resolved event, not typed here. This was the third copy of the
              * event's name in the client - after config/event.ts and the build-time id
              * - and the most visible one, on the cover of the app.
              *
              * The short name is split on its last space so the year can keep the
              * lighter weight wayfinder.css gives `em`. A short name with no space is
              * rendered whole rather than mangled.
              */}
            <h1 id="conference-title" className="login__mark">
              <span className="login__mark-short">
                {markHead(EVENT.shortName)}
                {markTail(EVENT.shortName) ? <em>{markTail(EVENT.shortName)}</em> : null}
              </span>
              {/*
                The full title used to sit under the wordmark in uppercase, two lines of
                it. It repeats what "CPD 2026" already says and was the single largest
                thing pushing the email field under the fold. It stays in the h1 for
                the accessible name, but visually hidden.
              */}
              <span className="sr-only">{EVENT.name}</span>
            </h1>
          </div>

          {/*
            * The rule under the wordmark is the heartbeat. A flat ECG line that
            * spikes once near the left, then runs on as a circuit trace and fans to
            * two pads at the right. One pulse travels it (CSS, so reduced-motion
            * applies). It replaces the plain border the colophon used to carry: the
            * same visual job, one line, and it says what the event is about.
            */}
          <svg className="login__rule" viewBox="0 -40 1000 168" aria-hidden="true" focusable="false">
            <defs>
              <filter id="login-rule-glow" x="-20%" y="-60%" width="140%" height="220%">
                <feGaussianBlur stdDeviation="2.4" result="b" />
                <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            <g fill="none" strokeLinecap="round" strokeLinejoin="round">
              {/* The source: a small outline heart the line leaves from. It beats once
                  as each pulse sets off. */}
              <path className="login__rule-heart"
                d="M40 66 C18 50 9 36 14 25 C19 15 32 14 40 25 C48 14 61 15 66 25 C71 36 62 50 40 66 Z" />
              {/* The track: main line, then two branches off a junction at x=760. */}
              <path className="login__rule-track" d="M66 44 H120 q11 -9 22 0 H158 l4 4 l6 -38 l6 52 l4 -18 H196 q15 -14 30 0 H760 H1140" />
              {/* Five traces leave the junction at 45 degrees; the outer two split again
                  further on. All run off the right edge of the cover. */}
              <path className="login__rule-track login__rule-track--branch" d="M760 44 l48 -48 H1140" />
              <path className="login__rule-track login__rule-track--branch" d="M760 44 l24 -24 H1140" />
              <path className="login__rule-track login__rule-track--branch" d="M760 44 l24 24 H1140" />
              <path className="login__rule-track login__rule-track--branch" d="M760 44 l48 48 H1140" />
              <path className="login__rule-track login__rule-track--branch login__rule-track--twig" d="M880 -4 l24 -24 H1140" />
              <path className="login__rule-track login__rule-track--branch login__rule-track--twig" d="M880 92 l24 24 H1140" />
              {/* The three traces run off the right edge of the cover, so the only
                  terminal is the junction; vias along the runs give the eye a foothold. */}
              <g className="login__rule-pad">
                <circle cx="760" cy="44" r="5" />
              </g>
              <g className="login__rule-core">
                <circle cx="760" cy="44" r="2" />
              </g>
              <g className="login__rule-via">
                <circle cx="880" cy="-4" r="2.8" /><circle cx="880" cy="92" r="2.8" />
                <circle cx="980" cy="-28" r="2.2" /><circle cx="980" cy="116" r="2.2" />
                <circle cx="900" cy="20" r="2.2" /><circle cx="1000" cy="20" r="2.2" />
                <circle cx="940" cy="44" r="2.2" /><circle cx="1040" cy="44" r="2.2" />
                <circle cx="900" cy="68" r="2.2" /><circle cx="1000" cy="68" r="2.2" />
                <circle cx="980" cy="-4" r="2.2" /><circle cx="980" cy="92" r="2.2" />
              </g>
              {/* The pulse, on the same geometry. */}
              <path className="login__rule-pulse" d="M66 44 H120 q11 -9 22 0 H158 l4 4 l6 -38 l6 52 l4 -18 H196 q15 -14 30 0 H760 H1140" filter="url(#login-rule-glow)" />
              <path className="login__rule-pulse login__rule-pulse--b1" d="M760 44 l48 -48 H1140" filter="url(#login-rule-glow)" />
              <path className="login__rule-pulse login__rule-pulse--b2" d="M760 44 l48 48 H1140" filter="url(#login-rule-glow)" />
              <path className="login__rule-pulse login__rule-pulse--b3" d="M760 44 l24 -24 H1140" filter="url(#login-rule-glow)" />
              <path className="login__rule-pulse login__rule-pulse--b4" d="M760 44 l24 24 H1140" filter="url(#login-rule-glow)" />
              <path className="login__rule-pulse login__rule-pulse--b5" d="M880 -4 l24 -24 H1140" filter="url(#login-rule-glow)" />
              <path className="login__rule-pulse login__rule-pulse--b6" d="M880 92 l24 24 H1140" filter="url(#login-rule-glow)" />
            </g>
          </svg>

          {/* The theme, one line, under the heartbeat. Three beats, like the pulse. */}
          <p className="login__theme" aria-label="Theme: Connect. Learn. Evolve.">
            <span>Connect.</span> <span>Learn.</span> <span>Evolve.</span>
          </p>

          <dl className="login__colophon">
            <div>
              <dt>Dates</dt>
              <dd className="tnum">{EVENT.dateLabel}</dd>
            </div>
            <div>
              <dt>Venue</dt>
              <dd>{EVENT.venue}</dd>
            </div>
          </dl>
        </section>

        <section className="login__auth" aria-label="Sign in">
          {sessionLost ? (
            <p className="field__error login__lost" role="alert">
              You were signed out because the server no longer recognised this device.
              Sign in again to continue.
            </p>
          ) : null}
          <SignInFlow stage={stage} setStage={setStage} email={email} setEmail={setEmail} />

          {/*
            One footer line, not three stacked orphans. The build number is for us, not
            for a delegate at the registration desk - same gate the Account footer uses.
          */}
          <p className="login__foot">
            Trouble signing in? Speak to the registration desk.
            {import.meta.env.DEV ? <span className="login__version">{APP_LABEL}</span> : null}
          </p>
        </section>
        </div>
      </div>
    </>
  )
}

interface FlowProps {
  stage: Stage
  setStage: (s: Stage) => void
  email: string
  setEmail: (e: string) => void
}

/*
 * Bring the field and the button under it into view once the keyboard has opened.
 * Browsers scroll the focused input to the edge of what is left, which puts the
 * Continue button behind the keys. Centring the input leaves room for its hint and
 * the button below. Delayed past the keyboard animation, or it measures the old
 * viewport. Bubbles from any input in the form.
 */
function revealSubmit(e: React.FocusEvent<HTMLElement>) {
  const target = e.target as HTMLElement
  if (!(target instanceof HTMLInputElement)) return
  window.setTimeout(() => {
    if (document.activeElement !== target) return
    target.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, 350)
}

function SignInFlow({ stage, setStage, email, setEmail }: FlowProps) {
  const { signIn } = useSession()
  const toast = useToast()
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /* Picks up the remaining time of a code sent before a reload. */
  const [secondsLeft, setSecondsLeft] = useState(() => {
    const r = readResume()
    return r ? Math.max(0, Math.ceil((r.sentAt + OTP_TTL_MS - Date.now()) / 1000)) : 0
  })

  // The API caches the OTP for two minutes; the countdown mirrors that exactly so
  // "Resend" never lights up while the previous code is still the valid one.
  useEffect(() => {
    if (secondsLeft <= 0) return
    const id = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000)
    return () => clearInterval(id)
  }, [secondsLeft])

  /**
   * Turn a thrown failure into something the person reading it can act on.
   *
   * Every call here used to report "could not reach", which is wrong whenever the
   * server did in fact answer, and it hid the difference between a dead API, a 500
   * and a request that timed out. The status is logged so a failure on someone's
   * phone can still be diagnosed afterwards.
   */
  function describe(err: unknown, fallback: string): string {
    if (err instanceof ApiError) {
      console.error(`[login] ${err.message}`)
      return err.status >= 500
        ? 'The sign-in service is having trouble. Try again in a moment.'
        : fallback
    }
    // AbortError is the client-side timeout in lib/api.ts, not a server response.
    if (err instanceof DOMException && err.name === 'AbortError') {
      console.error('[login] request timed out')
      return 'That took too long. Check your connection and try again.'
    }
    console.error('[login] network failure', err)
    return 'Could not reach the sign-in service. Check your connection.'
  }

  /** Ask the API who this address belongs to, and route to the matching second step. */
  async function identify() {
    setBusy(true)
    setError(null)
    try {
      const res = await api.requestOtp(email.trim())
      if (!res.user_status) {
        /*
          * Not "for this event": /otp looks at the `user` table and nothing else, so it
          * cannot know about events. It answers false when the address has no account
          * at all, or has one with no usable role.
          */
        setError('That address does not have an account.')
      } else if (res.role === ROLE_PARTICIPANT) {
        setStage('code')
        setSecondsLeft(120)
        writeResume({ email: email.trim(), sentAt: Date.now() })
        toast.show(`Code sent to ${email.trim()}`, 'ok')
      } else {
        // Role 4 — the API answered without sending anything. Staff use a password.
        setStage('password')
      }
    } catch (err) {
      setError(describe(err, 'That address could not be checked. Try again.'))
    } finally {
      setBusy(false)
    }
  }

  const verify = useCallback(
    async (value: string) => {
      setBusy(true)
      setError(null)
      try {
        const res = await api.verifyOtp(email.trim(), value)
        const session = toSession(res)
        if (session) {
          writeResume(null)
          signIn(session)
        } else {
          setError(
            res.otp === false
              ? 'That code is wrong or has expired.'
              : 'You are not registered for this event.',
          )
          setCode('')
        }
      } catch (err) {
        setError(describe(err, 'Could not verify the code. Try again.'))
      } finally {
        setBusy(false)
      }
    },
    [email, signIn],
  )

  async function submitPassword() {
    setBusy(true)
    setError(null)
    try {
      const res = await api.loginWithPassword(email.trim(), password)
      const session = toSession(res)
      if (session) signIn(session)
      else setError('Those details were not recognised.')
    } catch (err) {
      // An unknown address is an empty 200, which parses to {} rather than throwing,
      // so anything caught here is a genuine transport or server failure.
      setError(describe(err, 'Could not sign you in. Check the address and try again.'))
    } finally {
      setBusy(false)
    }
  }

  function restart() {
    writeResume(null)
    setStage('email')
    setCode('')
    setPassword('')
    setError(null)
    setSecondsLeft(0)
  }

  if (stage === 'email') {
    return (
      <form
        key="email"
        className="login__form"
        onFocus={revealSubmit}
        onSubmit={(e) => {
          e.preventDefault()
          void identify()
        }}
      >
        {/*
          "Sign in", not "Sign in to your CPD 2026 registration" - the cover prints CPD
          2026 at poster scale about 40px above this, so the long version was the same
          words twice and pushed the field further down for nothing.

          The kicker said "Participant access", which was not true. One adaptive flow
          serves both audiences here: the API's answer to the address decides whether
          the next step is a six-digit code or a staff password, and venue staff have
          no other way in. Labelling the screen for one of them was a leftover from
          when this was a row of method tabs.
        */}
        {/*
          No visible "Sign in" heading: the cover already prints the title 40px above,
          and a second serif heading competed with it. The form's aria-label carries
          the name; the field label is the first thing the eye lands on.
        */}
        <h2 className="sr-only">Sign in</h2>
        <Field
          label="Email address"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="name@uitm.edu.my"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={error}
          hint="Use the email you registered with."
          required
        />
        {/* One verb, one icon. The envelope promised email before the API had decided. */}
        <Button type="submit" variant="primary" busy={busy}>
          Continue
          <ArrowRight size={16} strokeWidth={2} className="btn__go" aria-hidden="true" />
        </Button>
        <GoogleFlow />
      </form>
    )
  }

  if (stage === 'password') {
    return (
      <form
        key="password"
        className="login__form"
        onFocus={revealSubmit}
        onSubmit={(e) => {
          e.preventDefault()
          void submitPassword()
        }}
      >
        <p className="login__addressed">
          Staff account · <strong>{email.trim()}</strong>
        </p>
        <Field
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={error}
          autoFocus
          required
        />
        <Button
          type="submit"
          variant="primary"
          busy={busy}
          disabled={!password}
          icon={<KeyRound size={17} strokeWidth={1.5} />}
        >
          Sign in
        </Button>
        <button type="button" className="login__back" onClick={restart}>
          <ArrowLeft size={13} strokeWidth={2} aria-hidden="true" /> Use a different address
        </button>
      </form>
    )
  }

  return (
    <div key="code" className="login__form" onFocus={revealSubmit}>
      <p className="login__addressed">
        Six-digit code sent to <strong>{email.trim()}</strong>
      </p>

      <OtpInput value={code} onChange={setCode} onComplete={verify} disabled={busy} autoFocus />
      {error ? (
        <p className="field__error" role="alert">
          {error}
        </p>
      ) : null}

      <Button
        variant="primary"
        busy={busy}
        disabled={code.length < 6}
        onClick={() => void verify(code)}
      >
        Verify and continue
      </Button>

      <div className="login__row">
        <button type="button" className="login__back" onClick={restart}>
          <ArrowLeft size={13} strokeWidth={2} aria-hidden="true" /> Change email
        </button>
        <button
          type="button"
          className="login__back"
          disabled={secondsLeft > 0}
          onClick={() => void identify()}
        >
          {secondsLeft > 0 ? (
            <>
              Resend in <span className="tnum">{secondsLeft}</span>s
            </>
          ) : (
            'Resend code'
          )}
        </button>
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------- google */

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined

interface GoogleCredentialResponse {
  credential?: string
}

/**
 * Google Identity Services renders its own button into a container we provide, and
 * hands back an ID token that `/validateGoogle` verifies server-side. The script is
 * loaded on demand rather than in index.html so the email path costs nothing.
 *
 * This sits below the primary action rather than behind a tab: it is an alternative,
 * not a peer, and an unconfigured build should not present an empty tab to click.
 */
function GoogleFlow() {
  const { signIn } = useSession()
  const holder = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'failed'>('idle')
  const [error, setError] = useState<string | null>(null)

  const handleCredential = useCallback(
    async (response: GoogleCredentialResponse) => {
      if (!response.credential) return
      setError(null)
      try {
        const res = await api.loginWithGoogle(response.credential)
        const session = toSession(res)
        if (session) {
          signIn(session)
        } else if (res.registered_user && res.event_registered === false) {
          setError('Your account is not registered for this event.')
        } else {
          setError('That Google account is not recognised.')
        }
      } catch {
        setError('Could not complete Google sign-in.')
      }
    },
    [signIn],
  )

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return
    let cancelled = false
    setStatus('loading')

    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://accounts.google.com/gsi/client"]',
    )
    const script = existing ?? document.createElement('script')

    function init() {
      if (cancelled) return
      const google = (window as unknown as { google?: any }).google
      if (!google?.accounts?.id || !holder.current) {
        setStatus('failed')
        return
      }
      try {
        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredential,
        })
        google.accounts.id.renderButton(holder.current, {
          theme: 'outline',
          size: 'large',
          shape: 'rectangular',
          width: holder.current.clientWidth || 320,
          text: 'continue_with',
        })
        setStatus('ready')
      } catch (err) {
        console.error('[login] Google Identity Services failed to initialise', err)
        setStatus('failed')
      }
    }

    function handleLoad() {
      init()
    }

    function handleError() {
      if (!cancelled) setStatus('failed')
    }

    // React StrictMode can run this effect twice while the shared script is still
    // loading. Do not treat an existing-but-unready script as a failure; subscribe to
    // its load event instead. This also makes hot reloads and slow mobile connections
    // behave like a normal loading state rather than silently removing Google.
    if (windowExistsWithGoogle()) {
      init()
    } else {
      script.addEventListener('load', handleLoad, { once: true })
      script.addEventListener('error', handleError, { once: true })
      if (!existing) {
        script.src = 'https://accounts.google.com/gsi/client'
        script.async = true
        script.defer = true
        document.head.appendChild(script)
      }
    }

    return () => {
      cancelled = true
      script.removeEventListener('load', handleLoad)
      script.removeEventListener('error', handleError)
    }
  }, [handleCredential])

  // An unconfigured build has no Google option. If it is configured but unavailable,
  // say so plainly instead of making the login page look incomplete or broken.
  if (!GOOGLE_CLIENT_ID) return null

  if (status === 'failed') {
    return (
      <div className="login__alt login__alt--unavailable">
        <p className="login__or" aria-hidden="true">
          <span>or</span>
        </p>
        <p className="login__google-status" role="status">
          Google sign-in is unavailable on this connection. Use your registered email instead.
        </p>
      </div>
    )
  }

  return (
    <div className="login__alt">
      <p className="login__or" aria-hidden="true">
        <span>or</span>
      </p>
      <div ref={holder} className="login__gsi" />
      {status === 'loading' ? <p className="login__note">Loading Google…</p> : null}
      {error ? (
        <p className="field__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}

function windowExistsWithGoogle(): boolean {
  const google = (window as unknown as { google?: any }).google
  return Boolean(google?.accounts?.id)
}

/** "CPD 2026" -> "CPD ". Everything up to and including the last space. */
function markHead(short: string): string {
  const at = short.lastIndexOf(' ')
  return at === -1 ? short : `${short.slice(0, at)} `
}

/** "CPD 2026" -> "2026", or '' when there is nothing to split off. */
function markTail(short: string): string {
  const at = short.lastIndexOf(' ')
  return at === -1 ? '' : short.slice(at + 1)
}
